import { CheckCircleTwoTone, QuestionCircleTwoTone, SmileTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { hygieneChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: "I don't like brushing my teeth because my gums hurt or bleed. What should I do?",
    answer: (
      <>
        If your gums hurt or bleed when brushing, it could be a sign of gum disease. Use a soft-bristled toothbrush and gentle brushing
        motions, and consider using an antibacterial mouthwash. Building this into a consistent morning and evening routine is a great way
        to practice the same habits you'll need for <Link to="/adulting-101/time-management">Time Management</Link>.
      </>
    ),
  },
  {
    question: 'How important is flossing and using mouthwash for my gums?',
    answer: (
      <>
        Very important. Flossing removes plaque between teeth that brushing can't reach, and mouthwash helps kill bacteria that cause gum
        disease and bad breath. Keeping floss and mouthwash stocked in the bathroom is also part of good{' '}
        <Link to="/adulting-101/household-care">Household Care</Link>.
      </>
    ),
  },
  {
    question: 'How often should I shower?',
    answer: (
      <>
        It's generally recommended to shower at least once a day, but this can vary depending on your activity level and personal
        preference. Keeping a consistent shower routine is a habit, just like the routines discussed in{' '}
        <Link to="/adulting-101/responsibility">Responsibility</Link>.
      </>
    ),
  },
  {
    question: 'Why should I change into clean clothes every day?',
    answer: (
      <>
        Wearing dirty clothes can lead to body odor, skin irritation, and an unkempt appearance. People respond to how you present yourself,
        so wearing clean clothes is a form of respect for others, similar to how you keep a shared space clean in{' '}
        <Link to="/adulting-101/household-care">Household Care</Link>.
      </>
    ),
  },
  {
    question: 'Why do I need to wash my hands so often?',
    answer: (
      <>
        Washing your hands prevents the spread of germs that cause colds, flu, and other illnesses, especially before eating and after using
        the restroom. It's a small habit that protects your health and the health of the people around you, which ties into being
        considerate in <Link to="/adulting-101/friends-and-family">Friends and Family</Link>.
      </>
    ),
  },
  {
    question: 'Is it okay to fart around other people?',
    answer: (
      <>
        Everyone farts — it's a normal bodily function. But try to excuse yourself to a private area when possible out of consideration for
        others. Reading the room and being aware of how your actions affect people is part of good{' '}
        <Link to="/adulting-101/communication-skills">Communication Skills</Link>.
      </>
    ),
  },
  {
    question: 'Is it okay to pick my nose in public?',
    answer: (
      <>
        Nobody wants to see you picking your nose, even though everyone does it sometimes. Do it privately in the bathroom and wash your
        hands afterward. Being mindful of how you're perceived by others is part of{' '}
        <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link>.
      </>
    ),
  },
  {
    question: 'Why should I cover my mouth when I cough or sneeze?',
    answer: (
      <>
        Covering your mouth helps prevent the spread of germs and shows consideration for the people around you. It's a simple habit that
        demonstrates respect, similar to the kind of consideration covered in{' '}
        <Link to="/adulting-101/problem-solving">Problem Solving</Link> when you think about how your actions affect others.
      </>
    ),
  },
]

export function Adulting101HygienePage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Hygiene</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #e6f7ff 0%, #ffffff 220px)' }}>
        <Title level={2}>Maintaining good hygiene is essential for your health and social life.</Title>
        <Paragraph type="secondary">
          Do you find people just don't want to be near you? No one wants to be around someone who neglects their hygiene.
        </Paragraph>

        <Card
          title={
            <Space>
              <SmileTwoTone />
              Hygiene Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, you meet the hygiene standards of a responsible adult!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Remember, maintaining good hygiene is an ongoing process. Keep up these habits daily to ensure your health and social
              well-being.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Hygiene
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
